import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  Box,
  Button,
  DrawerBody,
  DrawerFooter,
  Flex,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  Skeleton,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useRef } from 'react';
import ResizeTextarea from 'react-textarea-autosize';

import { FinleyAi as FinleyAiTypes } from '@finley-technologies/finley-types';

import { AthenaTableGQLType } from 'types/dashboard-core-gqlTypes.generated';

import {
  FinleyAiUserBadge,
  FinleyThinkingSpinner,
  OnClickSqlAction,
  PromptChatBubble,
  PromptSuggestionButton,
} from './FinleyAiThreadComponents';
import { FinleyAiThreadHistory } from './FinleyAiThreadWindow';
import {
  getPromptPlaceholder,
  OnClickAthenaSqlGenerationPromptSuggestion,
} from './FinleyAiUtils';
import { AthenaSqlGenerationViewContext } from './types';
import { useGetAthenaTablesForOrganizationQuery } from 'packages/athena/requests/requests.gql.generated';
import { AthenaTableSchemaSelectorAccordionPanel } from 'packages/athena/ui/AthenaSchemaSelector';
import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';

type FinleyAiPortfolioCovenantThreadStarterProps = {
  viewContext: AthenaSqlGenerationViewContext;
  athenaTables: AthenaTableGQLType[];
  threadStarterText: string;
};
const FinleyAiAthenaSqlGenerationThreadStarter = ({
  viewContext,
  athenaTables,
  threadStarterText,
}: FinleyAiPortfolioCovenantThreadStarterProps) => {
  return (
    <>
      <FinleyAiUserBadge label="Finley AI" type="finleyAi" />
      <Text my="16px">Here are the tables for SQL querying,</Text>
      <Box
        p="16px"
        backgroundColor={colors.background[50]}
        borderColor={colors.gray[200]}
        borderRadius="8px"
        borderWidth="1px"
      >
        <Accordion allowMultiple overflow="auto">
          {viewContext.tableNames.map((tableName) => {
            const athenaTable = _.find(
              athenaTables,
              (table) => table.tableName === tableName,
            );

            if (athenaTable) {
              return (
                <AccordionItem border="0" key={`accordion-item-${tableName}`}>
                  <AccordionButton width="100%">
                    <Box flex="1" overflow="hidden" textAlign="left">
                      <Text
                        fontWeight={500}
                        color={colors.black[95]}
                        isTruncated
                      >
                        {tableName}
                      </Text>
                    </Box>
                    <AccordionIcon
                      ml="8px"
                      flexShrink={0}
                      color={colors.black[95]}
                    />
                  </AccordionButton>
                  <Box ml="24px">
                    <AthenaTableSchemaSelectorAccordionPanel
                      athenaTable={athenaTable}
                    />
                  </Box>
                </AccordionItem>
              );
            }

            return null;
          })}
        </Accordion>
      </Box>
      <Text my="24px">{threadStarterText}</Text>
    </>
  );
};

type FinleyAiAthenaSqlGenerationPromptSuggestionsProps = {
  promptSuggestions: string[];
  onClickPromptSuggestion: OnClickAthenaSqlGenerationPromptSuggestion;
};
const FinleyAiAthenaSqlGenerationPromptSuggestions = ({
  promptSuggestions,
  onClickPromptSuggestion,
}: FinleyAiAthenaSqlGenerationPromptSuggestionsProps) => {
  return (
    <HStack alignItems="center" spacing="8px">
      {promptSuggestions.map((promptSuggestion) => (
        <PromptSuggestionButton
          key={promptSuggestion}
          label={promptSuggestion}
          onClick={() => onClickPromptSuggestion(promptSuggestion)}
        />
      ))}
    </HStack>
  );
};

type FinleyAiAthenaSqlGenerationDrawerChatViewProps = {
  viewContext: AthenaSqlGenerationViewContext;
  organizationId: string;
  thread: FinleyAiTypes.FinleyAiThreadWithPromptResponses | null;
  isThreadDisabled: boolean;
  isThreadFailed: boolean;
  isProducingMessage: boolean;
  isGeneratingPromptResponse: boolean;
  handleAthenaSqlExplain: (sql: string) => void;
  prompt: string;
  setPrompt: React.Dispatch<React.SetStateAction<string>>;
  onSendPrompt: () => void;
  onClickSqlAction: OnClickSqlAction;
  onClickSecondarySqlAction?: OnClickSqlAction;
  tempPrompt: string;
  threadStarterText?: string; // text to display to user to start the thread
  promptSuggestions?: {
    promptSuggestions: string[];
    onClickPromptSuggestion: OnClickAthenaSqlGenerationPromptSuggestion;
  }; // show prompt suggestions that users can send with one click
};
export const FinleyAiAthenaSqlGenerationDrawerChatView = ({
  viewContext,
  organizationId,
  thread,
  isThreadDisabled,
  isThreadFailed,
  isProducingMessage,
  isGeneratingPromptResponse,
  handleAthenaSqlExplain,
  prompt,
  setPrompt,
  onSendPrompt,
  onClickSqlAction,
  onClickSecondarySqlAction,
  tempPrompt,
  threadStarterText = 'What is your question?',
  promptSuggestions,
}: FinleyAiAthenaSqlGenerationDrawerChatViewProps) => {
  const toast = useToast();
  const promptInputTextArea = useRef<HTMLTextAreaElement>(null);

  const {
    data: athenaTablesData,
    loading: isAthenaTablesDataLoading,
  } = useGetAthenaTablesForOrganizationQuery({
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: { organizationId },
  });

  if (!athenaTablesData || isAthenaTablesDataLoading) {
    return (
      <>
        <DrawerBody>
          <Box borderWidth="1px" borderRadius="8px" p="16px">
            <FinleyAiUserBadge label="Finley AI" type="finleyAi" />
            <Text mt="24px">Here are the tables for SQL querying,</Text>
            <Box
              p="16px"
              backgroundColor={colors.background[50]}
              borderColor={colors.gray[200]}
              borderRadius="8px"
            >
              <Skeleton h="200px" />
            </Box>
          </Box>
        </DrawerBody>

        <DrawerFooter>
          <Input />
        </DrawerFooter>
      </>
    );
  }

  return (
    <>
      <DrawerBody h="100%">
        <Box
          borderWidth="1px"
          borderRadius="8px"
          p="24px"
          h="100%"
          overflow="auto"
        >
          <FinleyAiAthenaSqlGenerationThreadStarter
            viewContext={viewContext}
            athenaTables={athenaTablesData.athenaTables}
            threadStarterText={threadStarterText}
          />
          {!!promptSuggestions && (
            <Box mb="40px">
              <FinleyAiAthenaSqlGenerationPromptSuggestions
                promptSuggestions={promptSuggestions.promptSuggestions}
                onClickPromptSuggestion={
                  promptSuggestions.onClickPromptSuggestion
                }
              />
            </Box>
          )}
          {/* after user enters a prompt, before the worklease is generated and returns the thread
          we want to simulate the user's prompt successfully sending by showing the tempPrompt immediately */}
          {isGeneratingPromptResponse && !thread && tempPrompt && (
            <Flex flexDirection="column">
              <PromptChatBubble promptString={tempPrompt} />
              <Box mt="24px">
                <FinleyThinkingSpinner />
              </Box>
            </Flex>
          )}
          {!!thread && (
            <FinleyAiThreadHistory
              thread={thread}
              isThreadDisabled={isThreadDisabled}
              isProducingMessage={isProducingMessage}
              isGeneratingPromptResponse={isGeneratingPromptResponse}
              handleAthenaSqlExplain={handleAthenaSqlExplain}
              onClickSqlAction={onClickSqlAction}
              onClickSecondarySqlAction={onClickSecondarySqlAction}
              tempPrompt={tempPrompt}
            />
          )}
        </Box>
      </DrawerBody>

      <DrawerFooter mb="24px">
        <InputGroup>
          <Textarea
            as={ResizeTextarea}
            pr="56px"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={getPromptPlaceholder({
              type: 'athenaSqlGeneration',
              loading: isGeneratingPromptResponse,
              isThreadFailed,
            })}
            ref={promptInputTextArea}
            isDisabled={isGeneratingPromptResponse || isThreadFailed}
            resize="none"
            minRows={1}
            minH="20px"
          />
          <InputRightElement
            w="56px"
            height="100%"
            alignItems="flex-end"
            pb="4px"
          >
            <Button
              h="32px"
              w="44px"
              type="button"
              onClick={() => {
                onSendPrompt();
              }}
              isLoading={isGeneratingPromptResponse}
              isDisabled={isThreadFailed}
              backgroundColor={colors.aurora[400]}
              color="white"
            >
              <Icon icon="arrow-up" color="white" />
            </Button>
          </InputRightElement>
        </InputGroup>
      </DrawerFooter>
    </>
  );
};
